/**
 * The tab's left rail: the four funding artifacts in lifecycle order, each
 * showing its own status, plus the loan context every one of them prints.
 *
 * WHY A RAIL AND NOT COLUMNS. Each artifact here is a document with real width
 * requirements — a TFS prints Title and Correspondent as two columns
 * (`funding_statements.py` TableFundingStatement), the NWC prints sources
 * against uses, the Funding Shield renders a live PDF preview. Squeezed into a
 * third of the viewport they all become scroll. The rail keeps the *state* of
 * all four permanently visible (which is the only part you need at a glance,
 * and the reconciliation bar above already carries the numbers) and gives the
 * selected artifact the whole pane.
 *
 * This is a navigation/master rail, not a data table — the case CLAUDE.md calls
 * out as the exception to "every table is AG Grid". Its selected state is
 * level 1 (teal): picking an item changes which artifact you are looking at, the
 * same job the drilldown's own tab strip does.
 */
import PropTypes from "prop-types";

import Box from "@mui/material/Box";
import CheckRounded from "@mui/icons-material/CheckRounded";
import PriorityHighRounded from "@mui/icons-material/PriorityHighRounded";

import Flx from "../../../../components/layout/Flx";
import Txt from "../../../../components/typography/Txt";
import { formatDate } from "../../../../_src_shared/utils/functions/formatDate";
import { selectedStateSx } from "../../../../theme/activeStates";
import { level1 } from "../../../../theme/colors";
import { EMPTY, Pill, T, labelSx } from "./wireFundingShared";
import { hudTieOut } from "./WireReconciliationBar";

/**
 * Pane keys double as URL segments — the rail is route-driven, so every pane is
 * linkable (…/wire-and-funding/tie-out) and survives a refresh.
 */
export const PANES = {
  STATEMENT: "statement",
  TIE_OUT: "tie-out",
  TICKET: "wire-ticket",
  SHIELD: "funding-shield",
};

export const PANE_SLUGS = Object.values(PANES);

const BADGE_STYLE = {
  done: { ring: T.green, bg: T.greenTint, fg: T.green },
  alert: { ring: T.red, bg: T.redTint, fg: T.red },
  open: { ring: T.accent, bg: T.accentTint, fg: T.accentInk },
  idle: { ring: T.line, bg: T.card, fg: T.faint },
};

const StepBadge = ({ progress, index }) => {
  const s = BADGE_STYLE[progress] || BADGE_STYLE.idle;
  return (
    <Flx
      center
      sx={{
        flex: "none",
        width: 22,
        height: 22,
        borderRadius: "50%",
        bgcolor: s.bg,
        border: `1.5px solid ${s.ring}`,
        color: s.fg,
      }}
    >
      {progress === "done" ? (
        <CheckRounded sx={{ fontSize: 14 }} />
      ) : progress === "alert" ? (
        <PriorityHighRounded sx={{ fontSize: 14 }} />
      ) : (
        <Txt sx={{ fontSize: 11, fontWeight: 700, color: s.fg }}>{index}</Txt>
      )}
    </Flx>
  );
};

const RailItem = ({ item, index, selected, onSelect }) => (
  <Flx
    gap={1.25}
    onClick={() => onSelect(item.key)}
    sx={{
      px: 1.5,
      py: 1.25,
      // Top-aligned, not centred: the badge belongs to the step NAME, and the
      // pill row underneath makes the block two lines tall, so centring drifts
      // the number down between them.
      alignItems: "flex-start",
      cursor: "pointer",
      borderRadius: T.radiusSm,
      borderLeft: `2px solid ${selected ? level1.indicator : "transparent"}`,
      ...(selected
        ? selectedStateSx({
            level: 1,
            variant: "background",
            orientation: "vertical",
            underlinePosition: "left",
          })
        : { "&:hover": { background: level1.hoverTint } }),
    }}
  >
    <StepBadge progress={item.progress} index={index} />
    <Flx column gap={0.4} sx={{ minWidth: 0, flexGrow: 1 }}>
      <Txt
        sx={{
          fontSize: 12.5,
          fontWeight: selected ? 700 : 600,
          color: selected ? level1.text : T.ink,
          lineHeight: 1.25,
        }}
      >
        {item.title}
      </Txt>
      <Flx ac wrap gap={0.5}>
        <Pill label={item.status} tone={item.tone} />
        {item.hint ? (
          <Txt sx={{ fontSize: 10.5, color: T.faint }}>{item.hint}</Txt>
        ) : null}
      </Flx>
    </Flx>
  </Flx>
);

/** Where the wire ticket stands, from the six `loanTerms.wireInstructions` fields. */
export const ticketProgress = (wireInstructions) => {
  const w = wireInstructions || {};
  const filled = [
    w.wireRequestDate,
    w.wireAmount,
    w.wireStatus,
    w.wireFedRefNumber,
    w.closingWireReleaseDate,
    w.wireNotes,
  ].filter((v) => v != null && v !== "").length;

  if (w.wireStatus === "Released for Payment" || w.closingWireReleaseDate) {
    return { progress: "done", status: "Released", tone: "success" };
  }
  if (filled > 0) {
    return { progress: "open", status: "In progress", tone: "info" };
  }
  return { progress: "idle", status: "Not started", tone: "neutral" };
};

const FundingWorkRail = ({
  active,
  onSelect,
  statement,
  netWire,
  wireInstructions,
  loan,
}) => {
  // ── 1. Statement ─────────────────────────────────────────────────────────
  const status = statement?.statementStatus;
  const kind = statement?.statementKind || statement?.statementType;
  const statementItem = {
    key: PANES.STATEMENT,
    title: "Funding Statement",
    progress: !statement
      ? "idle"
      : status === "Acknowledged" || status === "Locked"
        ? "done"
        : "open",
    status: status || "Not generated",
    tone:
      status === "Acknowledged"
        ? "success"
        : status === "Locked"
          ? "warning"
          : status
            ? "info"
            : "neutral",
    hint: kind || null,
  };

  // ── 2. Tie-out ───────────────────────────────────────────────────────────
  const tieOut = hudTieOut(netWire);
  const tieOutItem = {
    key: PANES.TIE_OUT,
    title: "Net Wire Tie-out",
    progress:
      tieOut.state === "balanced"
        ? "done"
        : tieOut.state === "off"
          ? "alert"
          : "idle",
    status:
      tieOut.state === "balanced"
        ? "Balanced"
        : tieOut.state === "off"
          ? "Out of balance"
          : "No HUD lines",
    tone:
      tieOut.state === "balanced"
        ? "success"
        : tieOut.state === "off"
          ? "error"
          : "neutral",
    hint: netWire?.hudCompareSource === "prepared" ? "prepared" : null,
  };

  // ── 3. Wire ticket ───────────────────────────────────────────────────────
  const ticket = ticketProgress(wireInstructions);
  const ticketItem = {
    key: PANES.TICKET,
    title: "Wire Ticket",
    ...ticket,
    hint: wireInstructions?.wireFedRefNumber
      ? `ref ${wireInstructions.wireFedRefNumber}`
      : null,
  };

  // ── 4. Funding Shield ────────────────────────────────────────────────────
  // `fundingShieldStatus` has no pinned option list on the model yet
  // (closing.py:530, options 'tbd'), so whatever string the loan carries is
  // shown verbatim rather than mapped to a guessed vocabulary.
  const shieldStatus = loan?.fundingShieldStatus;
  const shieldRequested = loan?.fundingShieldRequestedDate;
  const shieldItem = {
    key: PANES.SHIELD,
    title: "Funding Shield",
    progress: shieldStatus ? "done" : shieldRequested ? "open" : "idle",
    status: shieldStatus || (shieldRequested ? "Requested" : "Not requested"),
    tone: shieldStatus ? "success" : shieldRequested ? "info" : "neutral",
    hint: shieldRequested
      ? formatDate(shieldRequested, "", "MMM d, yyyy")
      : null,
  };

  const items = [statementItem, tieOutItem, ticketItem, shieldItem];

  // Context every artifact prints — from the NWC first (it carries the funder
  // identity), falling back to the statement.
  const context = [
    {
      label: "Loan",
      value: netWire?.syncLoanId || statement?.syncLoanId || loan?.syncLoanId || EMPTY,
    },
    {
      label: "Funding entity",
      value: netWire?.fundingEntityName || EMPTY,
    },
    {
      label: "Channel",
      value: netWire?.fundingType || loan?.fundingType || EMPTY,
    },
    {
      label: "Purpose",
      value: netWire?.loanPurpose || loan?.loanPurpose || EMPTY,
    },
    {
      label: "Product",
      value:
        netWire?.loanProductType ||
        statement?.loanProductType ||
        loan?.loanProductType ||
        EMPTY,
    },
    {
      label: "Originator",
      value: statement?.originator || loan?.originator || EMPTY,
    },
  ];

  const cardSx = {
    bgcolor: T.card,
    border: `1px solid ${T.line}`,
    borderRadius: T.radius,
    boxShadow: T.shadow,
    overflow: "hidden",
  };

  return (
    // TWO cards, not one. The stepper is navigation and the context block is
    // reference; separating them stops the loan facts from reading as a fifth
    // step, and lets the stepper stay compact while the context grows.
    <Flx
      column
      gap={2}
      sx={{
        // Sticky against the page, so the four states stay visible while a long
        // statement or tie-out scrolls beside them.
        position: { lg: "sticky" },
        top: { lg: 8 },
      }}
    >
      <Flx column gap={0.25} component="nav" sx={{ ...cardSx, p: 1 }}>
        {items.map((item, i) => (
          <RailItem
            key={item.key}
            item={item}
            index={i + 1}
            selected={active === item.key}
            onSelect={onSelect}
          />
        ))}
      </Flx>

      <Box sx={{ ...cardSx, px: 2, py: 1.75 }}>
        <Txt sx={{ ...labelSx, display: "block", mb: 1.25 }}>
          Funding context
        </Txt>
        <Flx column gap={0.4}>
          {context.map((c) => (
            <Flx key={c.label} jb gap={1.5} ac sx={{ py: 0.3 }}>
              <Txt sx={{ fontSize: 12.5, color: T.muted }}>{c.label}</Txt>
              <Txt
                sx={{
                  fontSize: 12.5,
                  fontWeight: c.value === EMPTY ? 400 : 600,
                  color: c.value === EMPTY ? T.faint : T.ink,
                  textAlign: "right",
                  wordBreak: "break-word",
                  fontFamily: /^[A-Z0-9*-]+$/.test(String(c.value))
                    ? T.mono
                    : undefined,
                }}
              >
                {c.value}
              </Txt>
            </Flx>
          ))}
        </Flx>
      </Box>
    </Flx>
  );
};

FundingWorkRail.propTypes = {
  active: PropTypes.string,
  onSelect: PropTypes.func,
  statement: PropTypes.object,
  netWire: PropTypes.object,
  wireInstructions: PropTypes.object,
  loan: PropTypes.object,
};

export default FundingWorkRail;
